import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, RefreshCw, BookImage, Aperture, Lightbulb, ArrowDown, ArrowUp, Check } from "lucide-react";
import { ROOMS, APERTURE_OPTIONS, SHUTTER_OPTIONS, ISO_OPTIONS, TOTAL_ROOMS } from "../game/rooms";
import { evaluateShot, computeMeters, computeLivePreviewFx } from "../game/exposure";
import { pickCritique, FLAW_LABEL } from "../game/critiques";
import { getStoredSessionId, savePhoto, updateSession, generatePhotoImage } from "../game/api";
import Viewfinder from "../components/game/Viewfinder";
import CameraControls from "../components/game/CameraControls";
import Meters from "../components/game/Meters";
import PhantomPanel from "../components/game/PhantomPanel";
import Polaroid from "../components/game/Polaroid";
import AccessibilityBar from "../components/game/AccessibilityBar";
import Legend from "../components/game/Legend";

export default function GameScreen({
    ttsEnabled, setTTSEnabled,
    hcMode, setHCMode,
    audioEnabled, setAudioEnabled,
    practiceMode, setPracticeMode,
    tts, audio,
}) {
    const nav = useNavigate();
    const sessionId = getStoredSessionId();

    const [roomIndex, setRoomIndex] = useState(0);
    const [deaths, setDeaths] = useState(0);
    const [aperture, setAperture] = useState(APERTURE_OPTIONS[2]);
    const [shutter, setShutter] = useState(SHUTTER_OPTIONS[2]);
    const [iso, setIso] = useState(ISO_OPTIONS[0]);
    const [flash, setFlash] = useState(null);
    const [result, setResult] = useState(null);    // evaluation
    const [critique, setCritique] = useState(null);
    const [showPhantom, setShowPhantom] = useState(false);
    const [captured, setCaptured] = useState(false);
    const [aiImageUrl, setAiImageUrl] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);

    const room = ROOMS[roomIndex];

    useEffect(() => {
        if (!sessionId) { nav("/"); }
    }, [sessionId, nav]);

    // Ambient drone while in the darkroom
    useEffect(() => {
        if (audio?.startAmbient) audio.startAmbient();
        return () => { if (audio?.stopAmbient) audio.stopAmbient(); };
    }, [audio, audioEnabled]);

    const meters = useMemo(
        () => computeMeters({ shutter, iso, room }),
        [shutter, iso, room]
    );

    // Live visual preview fx — updates every time a dial is turned.
    const livePreviewFx = useMemo(
        () => computeLivePreviewFx({ room, aperture, shutter, iso }),
        [room, aperture, shutter, iso]
    );

    // Live exposure prediction (used in practice mode)
    const livePrediction = useMemo(() => {
        const playerEV = aperture.stops + shutter.stops - iso.stops;
        const diff = playerEV - room.targetEV;
        if (diff <= -2) return { label: "Very Under", icon: "down2", tone: "bad" };
        if (diff === -1) return { label: "Slightly Under", icon: "down", tone: "warn" };
        if (diff === 0)  return { label: "Correct Exposure", icon: "ok", tone: "good" };
        if (diff === 1)  return { label: "Slightly Over", icon: "up", tone: "warn" };
        return { label: "Very Over", icon: "up2", tone: "bad" };
    }, [aperture, shutter, iso, room]);

    // Stable photo descriptor for the result Polaroid
    const polaroidPhoto = useMemo(() => ({
        grade: result?.grade,
        aperture: aperture.label,
        shutter: shutter.label,
        iso: iso.label,
    }), [result, aperture, shutter, iso]);

    const fire = useCallback(async () => {
        const evalResult = evaluateShot({ room, aperture, shutter, iso });
        const crit = pickCritique(evalResult);

        // Shutter snap
        if (audio?.click) audio.click();

        // CRT level-up chime on success — pairs with the Debrief chip appearing
        if (evalResult.success && audio?.chime) audio.chime();
        // Discordant buzz on failure — pairs with the Phantom critique
        else if (!evalResult.success && audio?.buzz) audio.buzz();

        // Flash animation
        if (evalResult.exposure.includes("over")) setFlash("white");
        else if (evalResult.exposure.includes("under")) setFlash("black");
        else setFlash("white");
        setTimeout(() => setFlash(null), 350);

        setResult(evalResult);
        setCritique(crit);
        setCaptured(true);
        setAiImageUrl(null);

        // Phantom appears ONLY when not in practice mode (and not on success-only critique)
        if (!practiceMode) {
            setTimeout(() => {
                if (audio?.whisper) audio.whisper(1200);
                setShowPhantom(true);
                if (ttsEnabled && tts?.speak) tts.speak(crit.line);
            }, 500);
        }

        // Persist + maybe generate AI image
        try {
            const saved = await savePhoto(sessionId, {
                room_id: room.id,
                room_name: room.name,
                aperture: aperture.label,
                shutter: shutter.label,
                iso: iso.label,
                grade: evalResult.grade,
                success: evalResult.success,
                critique: crit.line,
                flaws: evalResult.flaws,
            });
            // Only generate AI image for successful (A / A+) captures
            if (evalResult.success && (evalResult.grade === "A" || evalResult.grade === "A+")) {
                setAiLoading(true);
                try {
                    const img = await generatePhotoImage(sessionId, saved.id);
                    if (img?.data_url) setAiImageUrl(img.data_url);
                } catch (e) {
                    // Image gen failed — silent fallback to CSS polaroid is intentional UX.
                    console.warn("Polaroid AI generation skipped:", e?.message || e);
                } finally {
                    setAiLoading(false);
                }
            }
        } catch (e) {
            // Persist failure is non-fatal — gameplay continues offline.
            console.warn("Photo persist failed:", e?.message || e);
        }
    }, [room, aperture, shutter, iso, sessionId, ttsEnabled, tts, audio, practiceMode]);

    const proceed = async () => {
        if (!result) return;
        setShowPhantom(false);

        if (result.success) {
            // advance
            if (roomIndex + 1 >= TOTAL_ROOMS) {
                try {
                    await updateSession(sessionId, { completed: true, current_room: TOTAL_ROOMS, deaths });
                } catch (e) {
                    console.warn("Session completion update failed:", e?.message || e);
                }
                nav("/end?outcome=win");
                return;
            }
            const next = roomIndex + 1;
            setRoomIndex(next);
            setCaptured(false);
            setResult(null);
            setCritique(null);
            setAiImageUrl(null);
            setAiLoading(false);
            try {
                await updateSession(sessionId, { current_room: next, deaths });
            } catch (e) {
                console.warn("Session room update failed:", e?.message || e);
            }
        } else {
            // retry — increment deaths
            const newDeaths = deaths + 1;
            setDeaths(newDeaths);
            // If Phantom alerted in boss room → instant Game Over
            const isPhantomAlert = result.flaws.includes("NOISE_TRIGGER") || (room.boss && !result.success);
            if (isPhantomAlert && newDeaths >= 3) {
                try {
                    await updateSession(sessionId, { deaths: newDeaths });
                } catch (e) {
                    console.warn("Session deaths update failed:", e?.message || e);
                }
                nav("/end?outcome=lose");
                return;
            }
            setCaptured(false);
            setResult(null);
            setCritique(null);
            setAiImageUrl(null);
            setAiLoading(false);
            try {
                await updateSession(sessionId, { deaths: newDeaths });
            } catch (e) {
                console.warn("Session deaths update failed:", e?.message || e);
            }
        }
    };

    const previewSettings = { aperture, shutter, iso };

    return (
        <div className="min-h-screen relative px-3 sm:px-6 lg:px-10 py-5" data-testid="game-screen">
            {/* Top bar */}
            <header className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <Aperture size={20} className="text-amber" />
                    <div className="text-[10px] tracking-[0.3em] uppercase">
                        <div className="text-amber">Room {roomIndex + 1} / {TOTAL_ROOMS}</div>
                        <div className="text-[var(--text-muted)]" data-testid="hud-deaths">Botched Negatives: {deaths}</div>
                    </div>
                </div>
                <RoomMap currentIndex={roomIndex} />
                <AccessibilityBar
                    ttsEnabled={ttsEnabled} setTTSEnabled={setTTSEnabled}
                    hcMode={hcMode} setHCMode={setHCMode}
                    audioEnabled={audioEnabled} setAudioEnabled={setAudioEnabled}
                    practiceMode={practiceMode} setPracticeMode={setPracticeMode}
                />
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 door-fade" key={room.id}>
                {/* Viewfinder + flavor */}
                <section className="lg:col-span-7 xl:col-span-8 flex flex-col gap-4">
                    <Viewfinder
                        room={room}
                        meters={meters}
                        fx={result?.fx}
                        flashState={flash}
                        captured={captured}
                        previewSettings={previewSettings}
                        livePreviewFx={livePreviewFx}
                        practiceMode={practiceMode}
                    />
                    <Legend />
                    <div className="border-l-2 border-amber-30 pl-4 py-1" data-testid="room-flavor">
                        <h2 className="font-display uppercase tracking-tight text-amber text-xl">{room.name}</h2>
                        <p className="text-sm text-[var(--text-primary)] mt-1 font-mono">{room.flavor}</p>
                        <p className="text-[10px] tracking-[0.25em] text-[var(--text-secondary)] mt-2 uppercase">
                            Teaches: {room.teaches}
                        </p>
                        {practiceMode && (
                            <div
                                className="mt-3 flex items-start gap-2 border border-[var(--crt-green)] bg-black/40 px-3 py-2 text-xs font-mono"
                                style={{ color: "var(--crt-green)" }}
                                data-testid="practice-hint"
                                role="note"
                                aria-live="polite"
                            >
                                <Lightbulb size={14} className="mt-px shrink-0" />
                                <span>{room.practiceHint}</span>
                            </div>
                        )}
                    </div>
                </section>

                {/* Controls panel */}
                <aside className="lg:col-span-5 xl:col-span-4 bg-bg2/60 border border-amber-30 p-5 lg:p-6 flex flex-col gap-5"
                       data-testid="controls-panel">
                    <CameraControls
                        aperture={aperture} shutter={shutter} iso={iso}
                        onChange={(p) => {
                            if (p.aperture) setAperture(p.aperture);
                            if (p.shutter) setShutter(p.shutter);
                            if (p.iso) setIso(p.iso);
                        }}
                        disabled={captured}
                    />
                    <Meters noise={meters.noise} blur={meters.blur} />
                    {practiceMode && (
                        <div
                            className="border border-[var(--crt-green)]/60 bg-black/40 px-3 py-2 flex items-center justify-between text-xs font-mono"
                            data-testid="exposure-prediction"
                            style={{ color: "var(--crt-green)" }}
                            aria-live="polite"
                        >
                            <span className="text-[10px] tracking-[0.25em] uppercase opacity-80">Live Exposure</span>
                            <span className="flex items-center gap-1.5">
                                {livePrediction.icon === "down2" && <><ArrowDown size={12} /><ArrowDown size={12} /></>}
                                {livePrediction.icon === "down" && <ArrowDown size={12} />}
                                {livePrediction.icon === "ok" && <Check size={12} />}
                                {livePrediction.icon === "up" && <ArrowUp size={12} />}
                                {livePrediction.icon === "up2" && <><ArrowUp size={12} /><ArrowUp size={12} /></>}
                                <span data-testid="exposure-prediction-label">{livePrediction.label}</span>
                            </span>
                        </div>
                    )}
                    <div className="flex items-center gap-4 justify-between border-t border-amber-30 pt-5">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-[var(--text-secondary)]">
                            <div>Frame Ready</div>
                            <div className="text-amber font-mono mt-1" data-testid="settings-readout">
                                {aperture.label} / {shutter.label} / ISO {iso.label}
                            </div>
                        </div>
                        <button
                            className="shutter-btn"
                            onClick={fire}
                            disabled={captured}
                            aria-label="Trigger shutter to take photograph"
                            data-testid="shutter-button"
                        >
                            <span className="text-sm">SHOOT</span>
                        </button>
                    </div>
                </aside>
            </div>

            {/* Result tray */}
            {captured && result && (
                <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5 door-fade" data-testid="result-tray">
                    <div className="bg-bg2/60 border border-amber-30 p-5 flex gap-5 items-center">
                        <Polaroid photo={{
                            grade: result.grade,
                            aperture: aperture.label,
                            shutter: shutter.label,
                            iso: iso.label,
                        }} fx={result.fx} room={room}
                           aiImageUrl={aiImageUrl}
                           aiLoading={aiLoading}
                        />
                        <div className="flex flex-col gap-3 flex-1">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-amber" data-testid="result-status">
                                {result.success ? "● Frame Approved" : "▲ Frame Rejected"}
                            </div>
                            <div className="font-display text-3xl text-amber" data-testid="result-grade">
                                Grade: {result.grade}
                            </div>
                            {result.flaws.length > 0 && (
                                <ul className="space-y-1" data-testid="result-flaws">
                                    {result.flaws.map(f => (
                                        <li key={f} className="text-xs font-mono text-phantom">
                                            ▌ {FLAW_LABEL[f] || f}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {result.success && room.winLesson && (
                                <div
                                    className="debrief-chip flex gap-2 text-xs"
                                    role="note"
                                    aria-label="Why that setting worked"
                                    data-testid="room-debrief"
                                >
                                    <span className="text-[9px] tracking-[0.3em] uppercase font-mono opacity-80 shrink-0 pt-px">
                                        Debrief
                                    </span>
                                    <span className="leading-snug">{room.winLesson}</span>
                                </div>
                            )}
                            <button
                                onClick={proceed}
                                className="mt-2 dial-btn selected px-5 py-3 text-xs flex items-center gap-2 self-start"
                                data-testid="proceed-button"
                            >
                                {result.success
                                    ? (roomIndex + 1 >= TOTAL_ROOMS ? "Escape" : "Next Room")
                                    : "Retry the Frame"}
                                <ChevronRight size={14} />
                            </button>
                        </div>
                    </div>

                    <PhantomPanel
                        critique={critique}
                        visible={showPhantom && !practiceMode}
                        onDismiss={() => setShowPhantom(false)}
                    />
                    {practiceMode && (
                        <div
                            className="border border-[var(--crt-green)] bg-black/40 p-5 flex flex-col gap-2"
                            data-testid="practice-feedback"
                            style={{ color: "var(--crt-green)" }}
                        >
                            <div className="text-[10px] tracking-[0.3em] uppercase opacity-80">Practice Mode · Phantom Muted</div>
                            <p className="font-mono text-sm leading-relaxed">
                                {result.success
                                    ? "Clean frame. You nailed the exposure — Phantom would have been speechless."
                                    : "Not yet. Compare your settings against the hint above and try again."}
                            </p>
                            {result.flaws.length > 0 && (
                                <ul className="text-xs space-y-0.5 opacity-90">
                                    {result.flaws.map(f => (
                                        <li key={f}>▌ {FLAW_LABEL[f] || f}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Footer */}
            <footer className="mt-10 flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)]">
                <button onClick={() => nav("/roll")} className="dial-btn px-3 py-1.5 flex items-center gap-1.5" data-testid="open-camera-roll">
                    <BookImage size={12} /> Camera Roll
                </button>
                <button onClick={() => window.location.reload()} className="dial-btn px-3 py-1.5 flex items-center gap-1.5" data-testid="restart-room">
                    <RefreshCw size={12} /> Reset
                </button>
            </footer>
        </div>
    );
}

const RoomMap = ({ currentIndex }) => (
    <div className="hidden md:flex items-center gap-1.5" data-testid="room-map" aria-label="Floorplan progress">
        {ROOMS.map((r, i) => (
            <div key={r.id} className="flex items-center gap-1.5">
                <div
                    className={`h-2 w-8 ${i < currentIndex ? "bg-amber" : i === currentIndex ? "bg-amber glow-amber-sm" : "bg-[var(--text-muted)]"}`}
                    title={r.name}
                />
                {i < ROOMS.length - 1 && <span className="text-[var(--text-muted)] text-[8px]">·</span>}
            </div>
        ))}
    </div>
);
